import React, { useEffect, useState } from 'react'
import Aside from 'components/Aside'
import {FormattedMessage, useIntl} from "react-intl";
import messages from "./messages";

const HandoverDetails = ({handoverId, onClose = () => {}}) => {
  const { formatMessage } = useIntl()
  const { formatDate, formatTime } = useIntl()

  if (!handoverId) {
    return null
  }

  const [state, setState] = useState({})
  const [actionable, setActionable] = useState(false)
  
  useEffect(() => {
      fetch(`/rest/v2/coordinator?pageSize=50`)
        .then(response => response.json())
        .then((coordinators) => {
          setState((state) => ({ ...state, coordinators }))
          return fetch(`rest/v2/handover/${handoverId}?includeRelations=1`)
        })
      .then((response) => response.json())
      .then(({ data: handover }) => {
        setState((state) => ({ ...state, handover }))
        setActionable(handover.actionable)

        return fetch(`/rest/v1/order/${handover.order_uid}?includeRelations=1`)
      })
      .then((response) => response.json())
      .then((order) => setState((state) => ({
        ...state,
        ...order,
        classification: order.classification,
        priority: order.priority,
        wordCount: order.word_count,
        playOutMinutes: order.play_out_minutes,
        handlingLanguage: order.language_uid,
        usage: order.orderUsageType == [] ? null : { value: order.orderUsageType.uid, label: order.orderUsageType.name },
        deadline: parseInt(order.deadline) > 0 ? `${formatDate(order.deadline * 1000)} ${formatTime(order.deadline * 1000)}` : null,
        expected: parseInt(order.expected) > 0 ? `${formatDate(order.expected * 1000)} ${formatTime(order.expected * 1000)}` : null,
        start: parseInt(order.orderusage_start) > 0 ? `${formatDate(order.orderusage_start * 1000)} ${formatTime(order.orderusage_start * 1000)}` : null,
        languagesCount: order.language_count || 0,
        medias: order.mediaTypes.map(({ name }) => name).join(', '),
        services: order.services.map(({ name }) => name).join(', '),
        countries: order.countries.map(({ name }) => name).join(', '),
        suppliers: order.suppliers.map(({ userid }) => userid).join(', '),
        suppliersCount: order.supplier_count || order.suppliers.length || 0,
        comments: order.comments,
        complex: order.complex,
      })))
      .catch((err) => {
        window.alert(err)
        console.error(err)
      })
  }, [])

  useEffect(() => {
    let interval

    if (handoverId) {
      interval = setInterval(() => {
        fetch(`/rest/v2/handover/${handoverId}/actionable`)
          .then((response) => response.json())
          .then(({ success, data }) => {
            if (success) {
              setActionable((prev) => {
                if (+data?.actionable !== +prev) {
                  return data?.actionable
                }
                return prev
              })
            }
          })
          .catch((error) => {
            console.error("Error fetching actionable data:", error)
          })
      }, 5000)
    }

    return () => clearInterval(interval)
  }, [handoverId])


  if (!state.handover || !state.uid || !state.coordinators) {
    return (
      <Aside>
        <div className="w-full h-full flex justify-center items-center">
          <i className="fa fa-sync animate-spin text-gray-600 text-2xl"></i>
        </div>
      </Aside>
    )
  }


  const trigger = (action, successMessage = '', prompt = null) => () =>
    fetch(`rest/v2/handover/${handoverId}/${action}`, {
      credentials: 'same-origin',
      method: 'PUT',
      body: JSON.stringify({
        handoverId: state.handover.uid,
        message: prompt ? window.prompt(prompt) : null,
      })
    })
      .then((response) => response.json())
      .then(({ success, errors }) => !success ? toastr.error(errors.join(',')) : toastr.success(successMessage))
      .then(() => {
        if (window.location.pathname == '/dashboard') {
          reactHistory.push('/reload-dashboard')
        } else {
          onClose()
        }
      })
      .catch((error) => toastr.error(error))


  const primaryCoordinator = state.coordinators.find(({ uid }) => uid == state.handover.customer.primary_coordinator_uid )
  const secondaryCoordinator = state.coordinators.find(({ uid }) => uid == state.handover.customer.secondary_coordinator_uid )

  return (
    <Aside onClose={onClose}>
      <div className="w-full h-full flex flex-col text-sm">
        <div className="relative grow overflow-scroll">
          <div className="absolute top-0 right-0 h-8 w-8 mt-6" onClick={onClose}>
            <i className="fa fa-times cursor-pointer"></i>
          </div>

          <div className="p-4 truncate mt-6 text-xl font-medium text-primary">
            { state.complex ? (
              <i className="fa fa-angle-double-up text-red-700" title="This is a complex deal"></i>
            ) : (
              <i className="fa fa-angle-down text-blue-300" title="This is a normal deal"></i>
            ) }
            <a href={`https://my.arnsbogroup.com/crm/deal/show/${state.handover.deal.ID}/`} target="_blank" className="cursor-pointer ml-2 text-xl">
              #{ state.handover.deal.ID } - { state.handover.deal.TITLE }
            </a>

            { !Boolean(Number(state.hidden)) &&
              <span>
                - <a href={`/orders/${state.uid}/overview`} className="cursor-pointer ml-2 text-xl">{ state.uid }</a>
              </span>
            }
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.company}/></span>
            <span>
              { state.handover.deal.company.TITLE }
              { !state.handover.customer &&
                  <sup className="text-sm ml-1 p-1 bg-gray-200 font-medium text-gray-700">NEW</sup>
              }
          </span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.netAmount}/></span>
            <span>{ state.handover.deal.OPPORTUNITY } { state.handover.deal.CURRENCY_ID }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.priority}/></span>
            <span>{ state.priority }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.classification}/></span>
            <span>{ state.classification }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.deadline}/></span>
            <span>{ state.deadline || formatMessage(messages.toBeDetermined) }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.salesResponsible}/></span>
            <span>{ state.handover.deal.responsible.NAME }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.products}/></span>
            <span>{ !state.handover.deal.products ? 'Not specified' : state.handover.deal.products.map(({ PRODUCT_NAME }) => PRODUCT_NAME).join(', ') }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.services}/></span>
            <span>{ state.services || formatMessage(messages.notSpecified) }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.expectedLanguages}/></span>
            <span>{ state.languagesCount || formatMessage(messages.notSupplied) }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.voiceTalents}/></span>
            <span>{ state.suppliers || formatMessage(messages.notSpecified) } - {state.languagesCount } in total</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.bookedVoiceTalents}/></span>
            <div>
              { state.handover.booked.map(({ supplier, dateStart, dateEnd }) => (
                <div key={supplier}>
                  { supplier } from { `${formatDate(dateStart * 1000)} ${formatTime(dateStart * 1000)}` } to { `${formatDate(dateEnd * 1000)} ${formatTime(dateEnd * 1000)}` }
                </div>
              )) }
            </div>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.preferredLanguages}/></span>
            <span>
              { {
                '17': 'English (South Africa)',
                '14': 'Danish or English (HQ)',
                '23': 'German (Andreas or HQ)',
              }[state.handlingLanguage] }
            </span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.deliveryPrimary}/></span>
            <span>{ primaryCoordinator ? primaryCoordinator.name : formatMessage(messages.notSpecified) }</span>
          </div>
          <div className="flex justify-between p-4 border-b border-gray-300">
            <span><FormattedMessage {...messages.deliverySecondary}/></span>
            <span>{ secondaryCoordinator ? secondaryCoordinator.name : formatMessage(messages.notSpecified) }</span>
          </div>

          <div className="text-center px-4 py-6 text-gray-500 font-medium uppercase">Log</div>
          <div>
            <div className="p-4 border-b border-gray-300">
              <div className="w-1/3 text-gray-900 font-bold mb-1">{ state.handover.created_at }</div>
              <div>Handover sent from sales to operations</div>
            </div>
            { state.handover.dispatches.map(({ uid, message, accepted_at, rejected_at, created_at, name, rejection_type }) => (
              <div className="p-4 border-b border-gray-300" key={uid}>
                <div className="w-1/3 text-gray-900 font-bold mb-1">{ accepted_at || rejected_at || created_at }</div>
                <div>
                  <span>
                      { {
                        USER: formatMessage(messages.declinedBy),
                        TIMER: formatMessage(messages.timedOutAt),
                        SEND_TO_DISPATCHER: formatMessage(messages.sendToDispatcher),
                        SEND_TO_SALES: formatMessage(messages.returnedToSalesBy),
                      }[rejection_type] || formatMessage(messages.currentlyAt) }
                  </span>
                  <span className="ml-1">{ name }</span>
                  { message &&
                      <span>
                        . { name.replace(/ .*/,'') } noted "<i>{ message }</i>".
                      </span>
                  }
                </div>
              </div>
            )) }
          </div>
        </div>

        {/*
          Accept is the only action left. Handovers are no longer offered to a particular
          coordinator, so there is nothing to decline, escalate, return or defer - an
          unclaimed handover is announced in the Bitrix dispatch chat and stays open until
          somebody takes it here.
        */}
        { actionable &&
          <div className="shrink-0 border-t border-gray-300 text-center truncate font-medium text-primary">
            <button
              className="w-full py-4 font-medium hover:bg-blue-100 cursor-pointer"
              onClick={trigger('accept', formatMessage(messages.handoverAccepted))}
            >
              <i className="fa fa-check"></i> <FormattedMessage {...messages.accept}/>
            </button>
          </div>
        }
      </div>
    </Aside>
  )
}

export default HandoverDetails
